import { useSession, sessionScope } from "@/lib/auth";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { apiGet, resolveApiUrl } from "@/lib/api";
import { createWorkflow, listWorkflowRuns, listWorkflows, rotateWorkflowSecret, updateWorkflow, type PublicWorkflow, type WorkflowTriggerType } from "./api";

export function WorkflowSettingsPanel() {
  const session = useSession();
  const scope = sessionScope(session);
  if (!session) return null;
  return <ScopedWorkflowSettingsPanel key={scope} scope={scope} />;
}

function ScopedWorkflowSettingsPanel({ scope }: { scope: string }) {
  const workflows = useQuery({ queryKey: ["workflows", scope], queryFn: listWorkflows });
  const rooms = useQuery({ queryKey: ["workflow-rooms", scope], queryFn: () => apiGet<{ rooms: { id: string; name: string | null; kind: string }[] }>("/v1/rooms") });
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState<WorkflowTriggerType>("incoming_webhook");
  const [roomId, setRoomId] = useState("");
  const [template, setTemplate] = useState("{{text}}");
  const [everyMinutes, setEveryMinutes] = useState("60");
  const [sourceRoom, setSourceRoom] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ secret: string; path: string; signing?: Record<string, string> } | null>(null);
  const [editing, setEditing] = useState<PublicWorkflow | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const history = useQuery({
    queryKey: ["workflow-runs", scope, historyId],
    queryFn: () => listWorkflowRuns(historyId!),
    enabled: !!historyId,
  });

  const selectableRooms = (rooms.data?.rooms ?? []).filter((room) => room.kind !== "dm" && room.kind !== "group_dm");
  const selectedRoom = roomId || selectableRooms[0]?.id || "";
  const sourceRooms = rooms.data?.rooms ?? [];

  const fill = (workflow: PublicWorkflow) => {
    setEditing(workflow);
    setName(workflow.name);
    setTrigger(workflow.trigger_type);
    setRoomId(String(workflow.action.room_id ?? ""));
    setTemplate(String(workflow.action.body_template ?? "{{text}}"));
    setEveryMinutes(String(workflow.trigger.every_minutes ?? 60));
    setSourceRoom(typeof workflow.trigger.room_id === "string" ? workflow.trigger.room_id : "");
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    setIssued(null);
    try {
      const triggerPayload =
        trigger === "schedule"
          ? { every_minutes: Number(everyMinutes) }
          : trigger === "message_created"
            ? { room_id: sourceRoom || null }
            : {};
      const body = {
        name,
        enabled: editing?.enabled ?? true,
        trigger_type: trigger,
        trigger: triggerPayload,
        action_type: "room_post",
        action: { room_id: selectedRoom, body_template: template },
      };
      if (editing) {
        await updateWorkflow(editing.id, body);
        setEditing(null);
      } else {
        const result = await createWorkflow(body);
        if (result.incoming_url_secret) {
          setIssued({ secret: result.incoming_url_secret, path: result.incoming_path || `/v1/workflows/incoming/${result.workflow.id}`, signing: result.signing });
        }
      }
      setName("");
      await workflows.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (workflow: PublicWorkflow) => {
    setBusy(true);
    setError(null);
    try {
      await updateWorkflow(workflow.id, { enabled: !workflow.enabled });
      await workflows.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const rotate = async (workflow: PublicWorkflow) => {
    setBusy(true);
    setError(null);
    try {
      const result = await rotateWorkflowSecret(workflow.id);
      setIssued({ secret: result.incoming_url_secret, path: result.incoming_path, signing: result.signing });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="workflows-heading" className="space-y-4">
      <h2 id="workflows-heading" className="font-semibold">自動化</h2>
      <p className="text-sm text-fg-secondary">Incoming webhook・定期・メッセージ作成をきっかけに、作成者の権限でルームへ投稿します。投稿先と、メッセージ監視の対象ルームは作成者が参加している部屋に限られます。</p>
      {(error || workflows.error) && <p role="alert" className="text-danger">{error || workflows.error?.message}</p>}
      {issued && (
        <div role="status" className="border rounded p-3 space-y-1 text-sm">
          <p>エンドポイント: <code>POST {new URL(resolveApiUrl(issued.path), window.location.origin).href}</code></p>
          <p>秘密（再表示できません）: <code>{issued.secret}</code></p>
          <p>ヘッダ: X-Aile-Timestamp / X-Aile-Event-Id / X-Aile-Signature</p>
          <p>署名: HMAC-SHA256(SHA256("workflow-webhook-v1:" + 秘密), timestamp + "." + eventId + "." + 本文)</p>
          <details>
            <summary>送信例（Node.js）</summary>
            <pre className="overflow-x-auto text-xs">{`import { createHash, createHmac, randomUUID } from "node:crypto";
const secret = ${JSON.stringify(issued.secret)};
const timestamp = String(Math.floor(Date.now() / 1000));
const eventId = randomUUID(); // 再送するときは同じ ID と本文を使う
const body = JSON.stringify({ text: "deploy finished" });
const key = createHash("sha256").update("workflow-webhook-v1:" + secret).digest();
const signature = createHmac("sha256", key).update(timestamp + "." + eventId + "." + body).digest("hex");
const response = await fetch(${JSON.stringify(new URL(resolveApiUrl(issued.path), window.location.origin).href)}, {
  method: "POST", body,
  headers: { "Content-Type": "application/json", "X-Aile-Timestamp": timestamp,
    "X-Aile-Event-Id": eventId, "X-Aile-Signature": signature }
});
console.log(response.status);`}</pre>
          </details>
        </div>
      )}
      <form
        className="border rounded p-4 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <label className="block text-sm">名前
          <Input value={name} onChange={(e) => setName(e.target.value)} required aria-label="ワークフロー名" />
        </label>
        <label className="block text-sm">トリガー
          <select className="w-full rounded border border-border-subtle bg-canvas p-2" value={trigger} disabled={!!editing} onChange={(e) => setTrigger(e.target.value as WorkflowTriggerType)} aria-label="トリガー">
            <option value="incoming_webhook">Incoming webhook</option>
            <option value="schedule">定期</option>
            <option value="message_created">メッセージ作成</option>
          </select>
        </label>
        {trigger === "schedule" && (
          <label className="block text-sm">間隔（分）
            <Input type="number" min={1} max={1440} value={everyMinutes} onChange={(e) => setEveryMinutes(e.target.value)} aria-label="間隔（分）" />
          </label>
        )}
        {trigger === "message_created" && (
          <label className="block text-sm">監視するルーム（未指定は、作成者が参加しているルームのみ。DM はルームを指定したときだけ）
            <select className="w-full rounded border border-border-subtle bg-canvas p-2" value={sourceRoom} onChange={(e) => setSourceRoom(e.target.value)} aria-label="監視ルーム">
              <option value="">参加中のルーム（DM を除く）</option>
              {sourceRooms.map((room) => (
                <option key={room.id} value={room.id}>{room.name || room.id}</option>
              ))}
            </select>
          </label>
        )}
        <label className="block text-sm">投稿先ルーム
          <select className="w-full rounded border border-border-subtle bg-canvas p-2" value={selectedRoom} onChange={(e) => setRoomId(e.target.value)} aria-label="投稿先ルーム">
            {selectableRooms.map((room) => (
              <option key={room.id} value={room.id}>{room.name || room.id}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">本文テンプレート
          <Input value={template} onChange={(e) => setTemplate(e.target.value)} aria-label="本文テンプレート" />
        </label>
        <p className="text-xs text-fg-muted">実行時の投稿者はワークフローの作成者です。</p>
        <div className="flex gap-2">
          <Button type="submit" disabled={busy || !name.trim() || !selectedRoom}>{busy ? "保存中…" : editing ? "変更を保存" : "ワークフローを保存"}</Button>
          {editing && <Button type="button" variant="ghost" disabled={busy} onClick={() => { setEditing(null); setName(""); }}>キャンセル</Button>}
        </div>
      </form>
      {workflows.isPending ? <p role="status">ワークフローを読み込んでいます…</p> : (
        <ul className="space-y-3">
          {(workflows.data?.workflows ?? []).map((workflow) => (
            <li key={workflow.id} className="border rounded p-4 space-y-2">
              <div className="flex justify-between gap-3">
                <div>
                  <p>{workflow.name}</p>
                  <p className="text-xs text-fg-muted">{workflow.trigger_type} · {workflow.enabled ? "有効" : "無効"} · 作成者 {workflow.created_by}</p>
                  {workflow.last_error && <p className="text-xs text-danger">失敗: {workflow.last_error}</p>}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => fill(workflow)}>編集</Button>
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => setHistoryId(workflow.id)}>履歴</Button>
                  {workflow.trigger_type === "incoming_webhook" && <Button size="sm" variant="secondary" disabled={busy} onClick={() => void rotate(workflow)}>秘密を再発行</Button>}
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => void toggle(workflow)}>{workflow.enabled ? "無効化" : "有効化"}</Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {historyId && (
        <div>
          <h3 className="font-semibold">実行履歴</h3>
          {history.isPending && <p role="status">履歴を読み込んでいます…</p>}
          {history.error && <p role="alert">{history.error.message}</p>}
          <ul className="space-y-2">
            {(history.data?.runs ?? []).map((run) => (
              <li key={run.id} className="text-sm">
                {run.status}: {run.error || run.message_id || "—"} ({new Date(run.created_at).toLocaleString()})
              </li>
            ))}
            {history.data && history.data.runs.length === 0 && <li>まだ実行がありません。</li>}
          </ul>
        </div>
      )}
    </section>
  );
}
